import { useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { sanitizeReturnTo, useLocalAuth } from '@wehunt/core-web'
import { COLORS, Icon, AuthShell, AuthHeader, SsoButton, Divider, AppleIcon, GoogleIcon, authInputStyle } from '@wehunt/ui'

// Create-account screen — mirrors the sign-in design. Email/password sign-up plus
// Apple/Google SSO. Token-themed and i18n-driven.
export function SignUpPage() {
  const { signUp, signInWithGoogle, signInWithApple, setRememberMe, state } = useLocalAuth()
  const { t } = useTranslation('app')
  const navigate = useNavigate()
  const { returnTo } = useSearch({ strict: false }) as { returnTo?: string }
  // Kept on the way to the other auth page, so a shared link opened while signed
  // out survives sign-in or sign-up, whichever the visitor picks.
  const authSearch = returnTo === undefined ? {} : { returnTo }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [busy, setBusy] = useState<null | 'email' | 'google' | 'apple'>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Where the visitor was heading before the guard sent them here. The value
    // came in on the URL, so it is sanitised to an on-origin path first: an
    // absolute or protocol-relative returnTo would make this an open redirect.
    if (state.status === 'signed-in') void navigate({ href: sanitizeReturnTo(returnTo) })
  }, [state.status])

  const mapError = (err: unknown): string => {
    const code = (err as { code?: string })?.code
    if (code === 'auth/email-already-in-use') return t('signUp.emailInUse')
    if (code === 'auth/weak-password') return t('signUp.weakPassword')
    return err instanceof Error && err.message ? err.message : t('genericError')
  }

  const runSso = async (kind: 'google' | 'apple', fn: () => Promise<void>) => {
    setError(null); setBusy(kind)
    try {
      await setRememberMe(true)
      await fn()
    } catch (err) {
      setError(mapError(err))
    } finally {
      setBusy(null)
    }
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password !== confirm) { setError(t('signUp.passwordsMismatch')); return }
    if (password.length < 6) { setError(t('signUp.weakPassword')); return }
    setBusy('email')
    try {
      await setRememberMe(true)
      await signUp(email, password)
    } catch (err) {
      setError(mapError(err))
    } finally {
      setBusy(null)
    }
  }

  return (
    <AuthShell>
      <AuthHeader eyebrow={t('signUp.eyebrow')} title={t('signUp.title')} subtitle={t('signUp.subtitle')} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SsoButton onClick={() => runSso('apple', signInWithApple)} disabled={busy !== null} icon={<AppleIcon />}>
          {t('signIn.continueWithApple')}
        </SsoButton>
        <SsoButton onClick={() => runSso('google', signInWithGoogle)} disabled={busy !== null} icon={<GoogleIcon />}>
          {t('signIn.continueWithGoogle')}
        </SsoButton>
      </div>

      <Divider>{t('signUp.orWithEmail')}</Divider>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('email')}
          aria-label={t('email')}
          autoComplete="email"
          style={authInputStyle}
        />
        <div style={{ position: 'relative' }}>
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('password')}
            aria-label={t('password')}
            autoComplete="new-password"
            style={{ ...authInputStyle, paddingRight: 46 }}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t('signIn.hidePassword') : t('signIn.showPassword')}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              width: 34, height: 34, border: 'none', background: 'transparent',
              color: COLORS.outline, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name={showPassword ? 'eye-off' : 'eye'} size={20} />
          </button>
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type={showConfirm ? 'text' : 'password'}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={t('signUp.confirmPassword')}
            aria-label={t('signUp.confirmPassword')}
            autoComplete="new-password"
            style={{ ...authInputStyle, paddingRight: 46 }}
          />
          <button
            type="button"
            onClick={() => setShowConfirm((v) => !v)}
            aria-label={showConfirm ? t('signIn.hidePassword') : t('signIn.showPassword')}
            aria-pressed={showConfirm}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              width: 34, height: 34, border: 'none', background: 'transparent',
              color: COLORS.outline, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon name={showConfirm ? 'eye-off' : 'eye'} size={20} />
          </button>
        </div>

        {error && <p role="alert" style={{ margin: '4px 0 0', color: COLORS.error, fontSize: 14 }}>{error}</p>}

        <button
          type="submit"
          className="wh-btn wh-btn-primary"
          disabled={busy !== null}
          style={{ height: 52, borderRadius: 12, fontSize: 16, fontWeight: 700, marginTop: 4 }}
        >
          {busy === 'email' ? t('signingIn') : t('signUp.submit')}
          <Icon name="arrow-right" size={18} />
        </button>
      </form>

      <div style={{ marginTop: 22, paddingTop: 20, borderTop: `1px solid ${COLORS.outlineVariant}`, textAlign: 'center', fontSize: 14, color: COLORS.onSurfaceVariant }}>
        {t('signUp.haveAccount')}{' '}
        <button type="button" onClick={() => navigate({ to: '/sign-in', search: authSearch })} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: COLORS.primary }}>
          {t('signUp.signIn')}
        </button>
      </div>
    </AuthShell>
  )
}
